import {Card, Collapse as $Collapse} from 'antd';
import {PaymentMethods, useStoresQuery} from 'api/generated';
import {BottomActions, CancelButton, Hint, PhoneNumberInput, Spacer, Stack} from 'components';
import {ScrollToError} from 'components/Form/ScrollToError';
import {useFormikContext} from 'formik';
import {
  Checkbox,
  DatePicker,
  Form,
  FormItem,
  Input,
  InputNumber,
  Select,
  SubmitButton
} from 'formik-antd';
import {useCountries} from 'hooks/useCountries';
import {useDefaultCurrency} from 'hooks/useDefaultCurrency';
import {usePaymentMethods} from 'hooks/usePaymentMethods';
import {paymentMethodLabels} from 'lib/labels';
import {arrayIncludesSome} from 'lib/utils';
import moment from 'moment-timezone';
import React, {useEffect} from 'react';
import styled from 'styled-components';
import {useQueryParam} from 'use-query-params';
import {
  PAY_METHODS_FULL_INFO_REQUIRED,
  PAY_METHODS_MANUAL_CAPTURE_ENABLED,
  PayByLinkFormValues
} from './common';
import {ContactDetailsFields} from './ContactDetailsFields';

const Collapse = styled($Collapse)``;

const Panel = styled($Collapse.Panel)`
  margin: 0 -16px;
`;

type Props = {};

export const PayByLinkForm: React.FC<Props> = () => {
  const [accountId] = useQueryParam<string>('accountId');
  const {data: storesData} = useStoresQuery({variables: {accountId}});
  const [paymentMethods, loadingPaymentMethods] = usePaymentMethods(accountId as string);
  const {currentCountry} = useCountries();
  const {currency, currencyFormat} = useDefaultCurrency(accountId);
  const {setFieldValue, values} = useFormikContext<PayByLinkFormValues>();

  const stores = storesData?.stores.items;
  const isBillingDetailsRequired = arrayIncludesSome(
    values.allowedPaymentMethods ?? [],
    PAY_METHODS_FULL_INFO_REQUIRED
  );

  const isManualCaptureEnabled =
    values.allowedPaymentMethods?.length !== 0 &&
    values.allowedPaymentMethods?.every((pm) =>
      PAY_METHODS_MANUAL_CAPTURE_ENABLED.includes(pm as PaymentMethods)
    );

  useEffect(() => {
    if (!loadingPaymentMethods) {
      setFieldValue('allowedPaymentMethods', paymentMethods);
    }
  }, [loadingPaymentMethods]);

  useEffect(() => {
    setFieldValue('hasBillingDetails', isBillingDetailsRequired);
  }, [isBillingDetailsRequired]);

  return (
    <Form layout="vertical" noValidate>
      <Card>
        <FormItem name="amount" label={'Amount'}>
          <InputNumber
            type="number"
            name="amount"
            style={{width: '100%'}}
            precision={2}
            addonAfter={currency}
          />
        </FormItem>
        <FormItem name="allowedPaymentMethods" label={'Allowed payment methods'}>
          <Select
            name="allowedPaymentMethods"
            mode="multiple"
            disabled={loadingPaymentMethods}
            loading={loadingPaymentMethods}>
            {paymentMethods.map((paymentMethod) => (
              <Select.Option key={paymentMethod} value={paymentMethod}>
                {paymentMethodLabels[paymentMethod]}
              </Select.Option>
            ))}
          </Select>
        </FormItem>
        <FormItem
          name="expireAt"
          label={
            <Stack gutter={5}>
              <span>Expiration date</span>
              <Hint
                message={
                  <>
                    <p>
                      The payment link will automatically expire after this date and will become
                      unavailable.
                    </p>
                    <span>
                      <b>NOTE:</b> This setting does not affect the expiration of authorized
                      (uncaptured) payments.
                    </span>
                  </>
                }
              />
            </Stack>
          }>
          <DatePicker
            format="ll"
            style={{width: '100%'}}
            allowClear={false}
            name="expireAt"
            showToday={false}
            disabledDate={(date) => (date ? date.isBefore(moment() as any) : false)}
          />
        </FormItem>
        {isManualCaptureEnabled && (
          <FormItem name="manualCapture">
            <Checkbox name="manualCapture">
              Manually capture payment{' '}
              <Hint
                message={
                  <>
                    <p>
                      Place a hold on the funds when the customer authorizes the payment, but don’t
                      capture the funds until later.
                    </p>
                    <span>
                      <b>NOTE:</b> Authorized (uncaptured) payments, by default, expire in 7 days.
                      But you can extend this by up to 30 days.
                    </span>
                  </>
                }
              />
            </Checkbox>
          </FormItem>
        )}
        <FormItem
          name="orderId"
          label={
            <Hint
              title={'Order ID'}
              message={
                <span>
                  The order ID in your platform (for example invoice number). If not present, MONEI
                  will generate a unique one for you.
                </span>
              }
            />
          }>
          <Input name="orderId" placeholder="12345" />
        </FormItem>
        <FormItem name="customer.name" label={'Customer name'}>
          <Input name="customer.name" placeholder="John Doe" />
        </FormItem>
        <FormItem name="customer.email" label={'Customer email'} validateTrigger={'blur'}>
          <Input name="customer.email" placeholder="email@example.com" />
        </FormItem>
        <FormItem name="customer.phone" label={'Customer phone'}>
          <PhoneNumberInput name="customer.phone" countryCode={currentCountry?.code ?? 'ES'} />
        </FormItem>
        <FormItem name="description" label={'Description'}>
          <Input name="description" placeholder={'Custom payment description'} />
        </FormItem>
        {stores && Boolean(stores.length) && (
          <FormItem name="storeId" label={'Store'}>
            <Select name="storeId" allowClear placeholder={'Select a store'}>
              {stores.map((store) => (
                <Select.Option key={store.id} value={store.id}>
                  {store.name}
                </Select.Option>
              ))}
            </Select>
          </FormItem>
        )}
        <Collapse
          {...(isBillingDetailsRequired ? {collapsible: 'disabled'} : {collapsible: 'header'})}
          {...(isBillingDetailsRequired ? {activeKey: ['hasBillingDetails']} : {})}
          onChange={(keys) => {
            setFieldValue('hasBillingDetails', keys.length > 0);
          }}
          ghost>
          <Panel header={'Billing Address'} key="hasBillingDetails">
            <ContactDetailsFields name="billingDetails" />
          </Panel>
        </Collapse>
        <Collapse
          onChange={(keys) => {
            setFieldValue('hasShippingDetails', keys.length > 0);
          }}
          ghost>
          <Panel header={'Shipping Address'} key="hasShippingDetails">
            <ContactDetailsFields name="shippingDetails" />
          </Panel>
        </Collapse>
        <ScrollToError />
      </Card>
      <BottomActions>
        <CancelButton backPath={`/accounts/${accountId}?tab=payments`}>{'Cancel'}</CancelButton>
        <Spacer />
        <SubmitButton disabled={false}>
          Create payment for {currencyFormat.format(values.amount)}
        </SubmitButton>
      </BottomActions>
    </Form>
  );
};
